import { Box } from "@mui/material";
import type { FC, ReactNode } from "react";
import { FONT_SIZE } from "../../../../theme/tokens";
import { SystemStepper } from "../../../UIToolkit/Inputs/SystemStepper";
import { EyebrowLabel } from "../../../UIToolkit/molecules/EyebrowLabel";
import { FocalCard } from "../../../UIToolkit/molecules/FocalCard";
import { POST_QUANTITY_MAX, POST_QUANTITY_MIN } from "./gapsPostShiftPrefill";

type Props = {
	/** The card's wash and edge (`FocalCard`). */
	ink: string;
	dataTestId: string;
	/** The eyebrow over the stepper. */
	label: string;
	/** The stepper's accessible name. */
	stepperLabel: string;
	value: number;
	onChange: (next: number) => void;
	disabled: boolean;
	stepperDataTestId: string;
	/** What the number is read against, right-aligned beside the stepper (its own eyebrow and figure). */
	aside?: ReactNode;
	asideDataTestId?: string;
};

/** The big figure an `aside` reads against — the same weight and numerals in every hero; each gives its own colour. */
export const QUANTITY_HERO_FIGURE_SX = { mt: 0.25, fontSize: FONT_SIZE.TITLE_MD, fontWeight: 700, fontVariantNumeric: "tabular-nums" } as const;

/**
 * A gaps sheet's one focal moment when it asks HOW MANY people a post is for: a big stepper in the classic post screen's
 * 1–100 range, inside a `FocalCard`, with what the number is weighed against beside it — the post sheet's window
 * (`filled/required`, short and posted) and the posted sheet's "was". One card, so the two sheets frame the same
 * question identically.
 */
export const GapsQuantityHero: FC<Props> = ({ ink, dataTestId, label, stepperLabel, value, onChange, disabled, stepperDataTestId, aside, asideDataTestId }) => (
	<FocalCard
		ink={ink}
		dataTestId={dataTestId}
		sx={{
			mx: 2,
			mt: 2,
			px: 2,
			py: 1.75,
			display: "flex",
			flexWrap: "wrap",
			alignItems: "flex-end",
			justifyContent: "space-between",
			columnGap: 2,
			rowGap: 1.5,
		}}
	>
		<Box>
			<EyebrowLabel>{label}</EyebrowLabel>
			<SystemStepper
				value={value}
				onChange={onChange}
				min={POST_QUANTITY_MIN}
				max={POST_QUANTITY_MAX}
				size="large"
				label={stepperLabel}
				disabled={disabled}
				dataTestId={stepperDataTestId}
				sx={{ mt: 0.75 }}
			/>
		</Box>
		{aside !== undefined && (
			<Box data-testid={asideDataTestId} sx={{ textAlign: "end", ml: "auto" }}>
				{aside}
			</Box>
		)}
	</FocalCard>
);
